<template>
  <div class="pa-4">
    <!-- Filters Card -->
    <v-card flat class="mb-4 pa-4 grey lighten-5 rounded-lg border">
      <v-row dense align="center">
        <v-col cols="12" sm="4" md="3">
          <v-text-field
            v-model="filters.search"
            label="بحث (اسم الراكب / الهاتف / التذكرة)"
            dense
            outlined
            hide-details
            clearable
            @keyup.enter="fetchNoShows"
            @click:clear="fetchNoShows"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-text-field
            v-model="filters.date_from"
            type="date"
            label="من تاريخ"
            dense
            outlined
            hide-details
            @change="fetchNoShows"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-text-field
            v-model="filters.date_to"
            type="date"
            label="إلى تاريخ"
            dense
            outlined
            hide-details
            @change="fetchNoShows"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-select
            v-model="filters.reason_code"
            :items="reasonOptions"
            item-text="text"
            item-value="value"
            label="سبب عدم الصعود"
            dense
            outlined
            hide-details
            clearable
            @change="fetchNoShows"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="4" md="3" class="d-flex align-center gap-2">
          <v-btn color="primary" @click="fetchNoShows" :loading="loading" class="elevation-1">
            <v-icon left>mdi-magnify</v-icon>
            تصفية
          </v-btn>
          <v-btn text color="grey darken-1" @click="resetFilters">
            إعادة ضبط
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-data-table
      :headers="headers"
      :items="noShows"
      :loading="loading"
      :server-items-length="totalItems"
      :options.sync="options"
      class="elevation-1 rounded-lg"
      hide-default-footer
    >
      <!-- Customer avatar & info -->
      <template v-slot:item.customer="{ item }">
        <div class="d-flex align-center py-2">
          <v-avatar size="38" color="grey lighten-2" class="me-3">
            <img v-if="item.customer && item.customer.avatar_url" :src="item.customer.avatar_url" alt="avatar" />
            <v-icon v-else color="grey darken-1">mdi-account</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-medium text-subtitle-2">
              {{ item.customer ? item.customer.name : 'عميل غير معروف' }}
            </div>
            <div class="caption text--secondary">
              {{ item.customer ? (item.customer.tel_number || item.customer.phone_number || 'بدون هاتف') : '' }}
            </div>
          </div>
        </div>
      </template>

      <!-- Trip & Route info -->
      <template v-slot:item.trip="{ item }">
        <div>
          <div class="font-weight-bold text-caption">
            رحلة #{{ item.planned_trip_id }}
          </div>
          <div class="caption primary--text" v-if="item.planned_trip && item.planned_trip.route">
            {{ item.planned_trip.route.name }}
          </div>
        </div>
      </template>

      <!-- Stop info -->
      <template v-slot:item.stop="{ item }">
        <v-chip small color="blue-grey lighten-5" class="blue-grey--text text--darken-2 font-weight-medium">
          <v-icon left small>mdi-map-marker</v-icon>
          {{ item.first_stop ? item.first_stop.name : ('محطة #' + item.start_stop_id) }}
        </v-chip>
      </template>

      <!-- Driver info -->
      <template v-slot:item.driver="{ item }">
        <div v-if="item.planned_trip && item.planned_trip.driver">
          <div class="caption font-weight-medium">
            {{ item.planned_trip.driver.name }}
          </div>
          <div class="caption text--secondary" v-if="item.planned_trip.bus">
            مركبة: {{ item.planned_trip.bus.name || item.planned_trip.bus.bus_number }}
          </div>
        </div>
        <span v-else class="caption grey--text">غير محدد</span>
      </template>

      <!-- Payment Badge -->
      <template v-slot:item.payment="{ item }">
        <div>
          <v-chip
            small
            :color="item.payment_method === 1 ? 'amber lighten-4' : 'blue lighten-5'"
            :class="item.payment_method === 1 ? 'amber--text text--darken-4' : 'blue--text text--darken-3'"
            class="font-weight-bold mb-1"
          >
            {{ item.payment_method === 1 ? 'نقدي (Cash)' : 'إلكتروني (Online)' }}
          </v-chip>
          <div class="caption font-weight-medium">
            المبلغ: {{ item.trip_price }} ج.م
          </div>
          <div class="caption text--secondary" v-if="item.payment_method === 1">
            {{ item.cash_collected_at ? 'تم التحصيل' : 'لم يتم التحصيل' }}
          </div>
        </div>
      </template>

      <!-- No-show Reason -->
      <template v-slot:item.reason="{ item }">
        <div>
          <v-chip
            small
            :color="item.no_show_reason_code === 'customer_not_present' ? 'deep-orange lighten-5' : 'purple lighten-5'"
            :class="item.no_show_reason_code === 'customer_not_present' ? 'deep-orange--text text--darken-3' : 'purple--text text--darken-3'"
            class="font-weight-bold mb-1"
          >
            {{ item.no_show_reason_code === 'customer_not_present' ? 'لم يحضر العميل' : 'سبب آخر' }}
          </v-chip>
          <div class="caption text--darken-2" v-if="item.no_show_reason_text">
            {{ item.no_show_reason_text }}
          </div>
        </div>
      </template>

      <!-- Timestamp -->
      <template v-slot:item.no_show_at="{ item }">
        <span class="caption text--secondary">
          {{ formatDate(item.no_show_at) }}
        </span>
      </template>
    </v-data-table>

    <!-- Pagination -->
    <div class="d-flex justify-end align-center pt-4">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="7"
        @input="fetchNoShows"
      ></v-pagination>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NoShowsTable',
  data() {
    return {
      loading: false,
      noShows: [],
      totalItems: 0,
      page: 1,
      pageCount: 1,
      options: {},
      filters: {
        search: '',
        date_from: '',
        date_to: '',
        reason_code: null,
      },
      reasonOptions: [
        { text: 'العميل لم يحضر (not_present)', value: 'customer_not_present' },
        { text: 'سبب آخر (other)', value: 'other' },
      ],
      headers: [
        { text: 'العميل', value: 'customer', sortable: false },
        { text: 'الرحلة', value: 'trip', sortable: false },
        { text: 'المحطة', value: 'stop', sortable: false },
        { text: 'السائق والمركبة', value: 'driver', sortable: false },
        { text: 'الدفع وحالة الحجز', value: 'payment', sortable: false },
        { text: 'سبب عدم الصعود', value: 'reason', sortable: false },
        { text: 'توقيت التسجيل', value: 'no_show_at', sortable: false },
      ],
    };
  },
  mounted() {
    this.fetchNoShows();
  },
  methods: {
    fetchNoShows() {
      this.loading = true;
      const params = {
        page: this.page,
        search: this.filters.search || undefined,
        date_from: this.filters.date_from || undefined,
        date_to: this.filters.date_to || undefined,
        reason_code: this.filters.reason_code || undefined,
      };

      axios
        .get('/reservations/no-shows', { params })
        .then((response) => {
          this.noShows = response.data.data || [];
          this.totalItems = response.data.total || 0;
          this.pageCount = response.data.last_page || 1;
        })
        .catch((error) => {
          console.error('Error fetching no-shows:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetFilters() {
      this.filters = {
        search: '',
        date_from: '',
        date_to: '',
        reason_code: null,
      };
      this.page = 1;
      this.fetchNoShows();
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      try {
        const date = new Date(dateStr);
        return date.toLocaleString('ar-EG', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (e) {
        return dateStr;
      }
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
.border {
  border: 1px solid #e0e0e0;
}
</style>
